import { useMemo, useState } from 'react'
import { Button } from '@nazka/nazka.mapframe.components'
import { useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import DynamicArrayInput from '@App/components/DynamicArrayInput/DynamicArrayInput';
import ImageUploadField from '@App/components/ImageUploadField/ImageUploadField';
import NewLocationMarker from '@App/components/MapMarkerLayer/NewLocationMarker';
import CustomHazardMenu from '@App/components/CustomHazardMenu/CustomHazardMenu';

import { useCreatePlan } from '@services/mutation.service';
import { useNetworkState } from '@services/hooks.service';
import { getSelectOptionIds } from '@services/utils.service';
import { useTranslation } from '@utils/i18n/i18n.consumers';
import { useOfflineUploadQueue } from '@utils/OfflineUploadQueue/OfflineUploadQueueProvider';
import { HAZARD_CATEGORIES } from '@App/components/AdaptationListComponent/adaptation-list-component.constants';
import { setLocationCoordinates, setAddress } from '@library/MaplibreMap/maplibre-map.slice';
import { trackEvent } from '@services/analytics.service';
import TranslatedSelect from '@App/components/TranslatedSelect/TranslatedSelect';

import './create-plan-form.styl'

function CreatePlanForm() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
    resetField,
  } = useForm()

  const createPlanMutation = useCreatePlan()
  const locationCoordinates = useSelector(state => state.maplibremap.locationCoordinates);
  const address = useSelector(state => state.maplibremap.address);

  const { t } = useTranslation()
  const isOnline = useNetworkState();
  const { enqueue } = useOfflineUploadQueue();

  const hazardOptions = useMemo(
    () => HAZARD_CATEGORIES.map(cat => ({ ...cat, label: t(cat.id) })),
    [t],
  );

  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState(null);

  function onSuccess() {
    navigate('/adaptation/plans');
    dispatch(setLocationCoordinates({ lat: null, lng: null }));
    dispatch(setAddress(null));
  }

  const onSubmit = async (data) => {
    const updatePayload = {
      translations: {
        en: {
          title: data.title,
          description: data.description,
          challenges: data.challenges,
          plan: data.plan,
          stakeholders: data.stakeholders,
        },
      },
      lat: locationCoordinates.lat,
      lng: locationCoordinates.lng,
      address,
      responsible: data.responsibility,
      hazardCategories: data.hazardCategories,
      creatorName: data.name,
      category: 'plan',
    };

    trackEvent('create_plan_submitted');

    // If offline, queue for later upload
    if (!isOnline) {
      await enqueue({
        type: 'plan',
        payload: {
          ...updatePayload,
          imageFile: imageFile || null,
        },
      });
      toast.info(t('You are offline. Your plan has been saved and will be uploaded when you reconnect.'));
      onSuccess();
      return;
    }

    // Online: upload immediately
    const formData = new FormData();
    Object.keys(updatePayload).forEach((key) => {
      if (['translations', 'hazardCategories'].includes(key)) {
        formData.append(key, JSON.stringify(updatePayload[key]));
      } else {
        formData.append(key, updatePayload[key]);
      }
    });

    if (imageFile) {
      formData.append('image', imageFile, imageFile.name);
    }

    createPlanMutation.mutate(formData, {
      onSuccess,
    })
  }

  return (
    <div className="create-plan-form__wrapper  my-fifth-adaptation-step">
      <h3>{t('Share your adaptation plan')}</h3>
      <div className="create-plan-form__title__wrapper">
        <label>
          {t('Title')}
          {' '}
          *
        </label>
        <input
          id="title"
          type="text"
          {...register('title', {
            required: t('Title is required.'),
            // minLength: { value: 3, message: 'Title must be at least 3 characters' },
          })}
          className={`create-plan-form__input ${errors.title ? 'create-plan-form__input--error' : ''
          }`}
          placeholder={t('Enter plan title')}
        />
        {errors.title && (
          <span className="create-plan-form__error">{errors.title.message}</span>
        )}
      </div>
      <ImageUploadField
        imageError={imageError}
        imageFile={imageFile}
        register={register}
        resetField={resetField}
        setImageError={setImageError}
        setImageFile={setImageFile}
      />

      <div className="create-story-form__address">
        <label htmlFor="address">
          {t('Address')}
          {' '}
          *
        </label>
        {address ? <span>{address}</span> : (
          <span className="create-story-form__address__placeholder">
            {t(isOnline ? 'Search for an address or click on the map to select location.' : 'Address not available offline.')}
          </span>
        )}
      </div>

      <div className="create-plan-form__image_wrapper">
        <label>
          {t('What are you trying to adapt in your community and why is it important?')}
          {' '}
          *
        </label>
        <textarea
          className="create-plan-form__textarea"
          placeholder={t('Enter your message here')}
          name="description"
          {...register('description', {
            required: t('Description is required.'),
            // minLength: { value: 10, message: 'Description must be at least 10 characters' },
          })}
        />
        {errors.description && (
          <span className="create-plan-form__error">{errors.description.message}</span>
        )}
      </div>
      <div className="create-plan-form__image_wrapper">
        <label>
          {t('Which hazard or extreme weather event can impact it?')}
          {' '}
          *
        </label>
        <Controller
          name="hazardCategories"
          control={control}
          rules={{
            required: t('Please select at least one linked hazard.'),
            validate: (value) => {
              if (!value || value.length === 0) {
                return t('Please select at least one linked hazard.');
              }

              return true;
            },
          }}
          render={({ field }) => (
            <TranslatedSelect
              className={`create-plan-form__select${errors.hazardCategories ? ' create-plan-form__select--error' : ''}`}
              rootElProps={{ id: 'hazardCategories' }}
              value={field.value || []}
              onInputChange={value => field.onChange(getSelectOptionIds(value))}
              options={hazardOptions}
              toggleButtonProps={{ roootElProps: { type: 'button' } }}
              isMulti
              placeholder={t('Select hazards...')}
              customMenuItems={CustomHazardMenu}
            />
          )}
        />
        {errors.hazardCategories && (
          <span className="create-plan-form__error">{errors.hazardCategories.message}</span>
        )}
      </div>
      <div className="create-plan-form__image_wrapper">
        <DynamicArrayInput
          name="plan"
          control={control}
          label={t('What is your action plan for adapting it? List the different steps of your plan and the stakeholders that need to be involved.')}
          required
          placeholder={t('Add step')}
          addButtonText={t('Add step')}
          minItems={1}
          maxItems={10}
          errors={errors.plan}
        />
      </div>
      <div className="create-plan-form__field">
        <label htmlFor="stakeholders" className="create-plan-form__label">
          {`${t('Which stakeholders need to be involved?')} *`}
        </label>
        <textarea
          id="stakeholders"
          rows={4}
          {...register('stakeholders', {
            required: t('Stakeholders is required.'),
            // minLength: { value: 2, message: t('Stakeholders must be at least 2 characters') },
          })}
          className={`create-plan-form__input ${errors.stakeholders ? 'create-plan-form__input--error' : ''
          }`}
          placeholder={t('Which stakeholders need to be involved?')}
        />
        {errors.stakeholders && (
          <p className="create-plan-form__error">{errors.stakeholders.message}</p>
        )}
      </div>
      <div className="create-plan-form__image_wrapper">
        <label>
          {t('What are the challenges to execute the adaptation action plan? Can you come up with any solutions for these challenges?')}
          {' '}
          *
        </label>
        <textarea
          className="create-plan-form__textarea"
          placeholder={t('Enter your message here')}
          name="challenges"
          {...register('challenges', {
            required: t('Challenges is required.'),
            // minLength: { value: 10, message: t('Challenges must be at least 10 characters') },
          })}
        />
        {errors.challenges && (
          <span className="create-plan-form__error">{errors.challenges.message}</span>
        )}
      </div>
      <div className="create-plan-form__image_wrapper">
        <label>
          {t('Who is responsible for this adaptation plan?')}
          {' '}
          *
        </label>
        <textarea
          className="create-plan-form__textarea"
          placeholder={t('Enter your message here')}
          name="responsibility"
          {...register('responsibility', {
            required: t('Responsibility is required.'),
            // minLength: { value: 10, message: 'Responsibility must be at least 10 characters' },
          })}
        />
        {errors.responsibility && (
          <span className="create-plan-form__error">{errors.responsibility.message}</span>
        )}
      </div>
      <div className="create-plan-form__image_wrapper">
        <label>
          {t('Name')}
          {' '}
          *
        </label>
        <input
          placeholder={t('Enter your name')}
          className="create-plan-form__input-name"
          {...register('name', {
            required: t('Name is required.'),
            // minLength: { value: 3, message: 'Name must be at least 3 characters' },
          })}
        />
        {errors.name && (
          <span className="create-plan-form__error">{errors.name.message}</span>
        )}
      </div>
      <div className="create-plan-form__submit__buttons my-seventh-adaptation-step">
        <Button
          css={{
            borderRadius: '10px',
            fontSize: '12px',
            '&:hover': {
              color: 'white',
            },
          }}
          layout="outlined"
          onClick={() => {
            navigate('/adaptation/plans')
            dispatch(setLocationCoordinates({ lat: null, lng: null }));
            dispatch(setAddress(null));
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          css={{
            borderRadius: '10px',
            fontSize: '12px',
            color: 'white',
            '&:hover': {
              color: 'white',
            },
          }}
          loaderProps={{
            css: {
              backgroundColor: 'transparent',
            },
          }}
          disabled={!isValid || isSubmitting || !locationCoordinates.lat || !locationCoordinates.lng}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t(isOnline ? 'Submit' : 'Save offline')}
        </Button>
      </div>
      <NewLocationMarker />
    </div>
  )
}

export default CreatePlanForm
